import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { openOverlays$ } from '@schmancy/overlay'
import {
	AT_REST,
	commits,
	dismissed,
	dragState,
	DragVelocity,
	lockIntent,
	type DragEnd,
	type DragIntent,
} from '@schmancy/overlay/overlay.dismiss-law'
import { SNAP_BACK_MS } from '@schmancy/overlay/overlay.gestures'
import { DURATION_EXIT, EASE_EMPHASIZED_DECELERATE, EASE_IN } from '@schmancy/utils/animation'
import { css, html, type PropertyValues } from 'lit'
import { property, query, state } from 'lit/decorators.js'
import {
	Subject,
	debounceTime,
	distinctUntilChanged,
	filter,
	from,
	fromEvent,
	map,
	merge,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs'
import '../progress/progress'
import '../typography/typography'

export interface LightboxChangeDetail {
	index: number
}

export interface LightboxZoomDetail {
	scale: number
}

interface Point {
	x: number
	y: number
}

type Gesture =
	| { kind: 'swipe'; start: Point }
	| { kind: 'pan'; start: Point; origin: Point }
	| { kind: 'pinch'; distance: number; scale: number; anchor: Point }

const MIN_SCALE = 1
const MAX_SCALE = 4
const DOUBLE_TAP_SCALE = 2
const ZOOM_STEP = 0.5
const ZOOM_TRANSITION_MS = 200
const SWIPE_THRESHOLD = 50
const TAP_MOVE_THRESHOLD = 10
const TAP_MAX_DURATION = 250
const DOUBLE_TAP_WINDOW = 300
const DOUBLE_TAP_DISTANCE = 40
/** A wheel gesture has no discrete end event (unlike pointerup for a pinch),
 *  so `settleZoom()` fires this long after the last wheel tick instead. */
const WHEEL_SETTLE_IDLE_MS = 150
/** Trackpad pinch reports deltaY in the same rough range as a mouse wheel
 *  notch; this converts it to the same per-step magnitude `+`/`-` use. */
const WHEEL_ZOOM_SENSITIVITY = ZOOM_STEP / 100
/** How much of the picture stays on screen however far it is panned. Panning is
 *  otherwise unbounded — the picture goes anywhere over the stage rather than
 *  only as far as its own edges — but one panned entirely out of view is one the
 *  reader has lost, with only a double-tap they may not know about to bring it
 *  back. */
const PAN_KEEP_VISIBLE_PX = 48
/**
 * `panX`/`panY`/`scale` are plain instance fields, not `@state`. A pinch or
 * drag fires a pointermove every few milliseconds; routing that through Lit's
 * reactive-property diff would re-run `render()` on every frame of a gesture
 * for a value only one element ever reads. Continuous gesture frames write
 * `--lightbox-x`/`--lightbox-y`/`--lightbox-scale` straight onto `.media` via
 * `applyTransform()`; only a settled change (index, load state, a zoom that
 * has finished) goes through `@state` and a real render.
 *
 * For the same reason `.media`'s `style` attribute carries no binding: a bound
 * attribute is rewritten whole by the render a settled zoom triggers, which
 * strips the inline `transition` that zoom is still running.
 */
@schmancyElement('schmancy-lightbox')
export class SchmancyLightbox extends SchmancyElement {
	static styles = [
		css`
			/* Whatever box it is given, whole. The viewer layout hands it a box
			 * shrink-wrapped to the picture and placed at the gesture that opened it,
			 * the same way it places every other anchored surface — so this element
			 * sizes to what it is handed and draws nothing around it.
			 *
			 * There used to be a square here (a fixed width plus aspect-ratio: 1/1),
			 * fixed before the image loaded so the overlay opened at its final size
			 * and never resized under the reader. What it did instead was SHOW — as
			 * bars beside a letterboxed photo, and straight THROUGH a
			 * transparent-background one. The caps live on the picture itself now, so
			 * its box is the picture at every aspect and there is no gap to show.
			 *
			 * position: relative stays, and is not decoration: the layer below is
			 * absolute inset-0, and without a positioned host it would resolve
			 * against whatever ancestor happens to be positioned — the overlay
			 * surface inside an overlay, and anything at all outside one. */
			:host {
				position: relative;
				display: grid;
				/* STRETCH, not center. Centring here shrink-wraps the stage to the
				 * picture, and the stage is the box clampPan() measures a zoom
				 * against — so a 448px photo on a 1280x720 screen could only ever be
				 * pinched inside its own 448px square, with the rest of the screen
				 * unreachable. The stage takes the whole box it is given and centres
				 * the picture INSIDE it, which it already does with its own
				 * place-items-center; this one was centring the centring. */
				place-items: stretch;
				min-width: 0;
				min-height: 0;
				/* One origin for the whole lifetime — the point the surface bloomed
				 * out of, which the overlay wrote as viewport percentages and which
				 * inherits down to here. The drag, the snap-back and the dismissal
				 * are then pure interpolations of (translate, scale) about one
				 * pinned point, and none of them can jump. Standalone, with no
				 * overlay above, the fallback is the element's own centre, which is
				 * the right answer for a viewer nothing was touched to open. */
				transform-origin: var(--schmancy-overlay-origin-x, 50%) var(--schmancy-overlay-origin-y, 50%);
			}
			/* Only the filtered picture may fade: opacity below 1 on an ancestor
			 * creates a backdrop root and cuts its blur off from the page. */
			/* Shrink-wrapped to the picture, and given no shape of its own. It used to
			 * carry the width, the caps and the aspect-ratio, which made its box a
			 * RECTANGLE THE PICTURE DOES NOT FILL whenever the width and the height cap
			 * both bound: the delivery note resolved to a 1241x648 frame for an image
			 * of ratio 1.08, and object-contain letterboxed inside it. That gap is
			 * invisible while nothing is drawn there and fatal the moment something is
			 * — it is exactly what the removed square showed as bars beside a photo.
			 * The caps live on the picture itself now, so the box IS the picture at
			 * every aspect and every cap. */
			/* Shrink-wrapped to the picture, and placed by nobody here. WHERE the
			 * picture opens is the surface's question, and the surface is placed at the
			 * anchor by the same Floating UI pass every other show() surface goes
			 * through — so this box simply sits in the stage it is given. A placement of
			 * its own was tried here and deleted: it was shift() written a second time,
			 * worse, and it put the picture somewhere show would not have. */
			.frame {
				position: relative;
				display: block;
				width: fit-content;
				height: fit-content;
			}
			/* THE PICTURE, and its own ground.
			 *
			 * A transparent PNG has no visible bounds, so with nothing drawn anywhere it
			 * floats with no edge at all. This fill gives it one — and it is not a thing
			 * drawn AROUND the picture, which is the treatment that was rejected three
			 * times. It is exactly coincident with the picture's box: the print, not a
			 * mat around the print. Nothing of it extends past the image, at any aspect,
			 * because the sizing below makes this box the rendered picture.
			 *
			 * It is on THIS element and not on .frame, and that is load-bearing. The drag
			 * is written to the host, so anything inside follows it either way — but the
			 * pan and the zoom are written only here, as --lightbox-x/y/scale. A ground
			 * on .frame would sit dead still while the picture scaled and slid across it,
			 * which is precisely how a backing announces itself as a separate, static
			 * thing. Sharing one element means sharing one matrix: it cannot drift,
			 * because there is nothing for it to drift from. The dismissal fade reaches
			 * it too, through the image's own opacity.
			 *
			 * Sized by the file, capped by the screen, with no aspect-ratio box around
			 * it: a replaced element under both caps shrinks on its own intrinsic ratio,
			 * so the box stays the picture and never letterboxes. The ratio is still
			 * declared for the moment before the bytes arrive, when there is no
			 * intrinsic size to size from yet. */
			.media {
				display: block;
				width: auto;
				height: auto;
				max-width: calc(100dvw - 2rem);
				max-height: 90svh;
				aspect-ratio: var(--lightbox-aspect, 1);
			}
			/* Keep the overlay's glass material stable through motion: switching blur
			 * off during a gesture and restoring it afterward visibly changes the ground.
			 * It stays on the image so transparent files gain bounds without a mat. */
			.media {
				background: color-mix(
					in srgb,
					var(--schmancy-sys-color-surface-container, Canvas) var(--schmancy-sys-glass-fill, 50%),
					transparent
				);
				-webkit-backdrop-filter: blur(var(--schmancy-sys-glass-blur, 24px))
					saturate(var(--schmancy-sys-glass-saturate, 180%));
				backdrop-filter: blur(var(--schmancy-sys-glass-blur, 24px)) saturate(var(--schmancy-sys-glass-saturate, 180%));
			}
		`,
	]

	/** Single-image source. Ignored when `images` is non-empty. */
	@property({ type: String }) src = ''
	/** Gallery sources. Resolution variants of one work belong in `srcset`, not here. */
	@property({ type: Array }) images: string[] = []
	@property({ type: Number }) initialIndex = 0
	/** Fallback accessible name for every image. */
	@property({ type: String }) label = ''
	/** Per-image accessible names, indexed with `images`. */
	@property({ type: Array }) labels: string[] = []

	@state() private currentIndex = 0
	@state() private loading = true
	@state() private failed = false
	@state() private zoomAnnouncement = ''

	// `| null`, not `?`: Lit's `@query` getter is `renderRoot?.querySelector() ?? null`,
	// so a missing node is NULL and never undefined. Typed optional, a guard reads
	// `!== undefined`, admits the null, and dereferences it — see the two filters in
	// `connectedCallback`, where that killed the whole merged subscription.
	@query('.stage') private stage!: HTMLElement | null
	@query('.media') private media!: HTMLImageElement | null

	private scale = MIN_SCALE
	private panX = 0
	private panY = 0
	private overlayOpacityAnimation?: Animation

	private pointers = new Map<number, Point>()
	private gesture?: Gesture
	private gestureStartTime = 0
	/** What the drag turned out to mean, decided once and held to the release. */
	private dragIntent: DragIntent = 'undecided'
	/** The scored travel of the current drag, as of its last frame. */
	private dragDistance = 0
	private dragVelocity = new DragVelocity()
	/** Sampled ONCE at the press. The layout viewport and the visual viewport
	 *  differ by the mobile URL bar, and this element already listens for the
	 *  resize that bar fires mid-gesture — re-reading it would re-scale the
	 *  commit distance under a finger already travelling toward it. */
	private pressViewportHeight = 0
	/** The bloom origin in viewport px, recovered at the press. */
	private pressOrigin: Point = { x: 0, y: 0 }
	private lastTap?: { time: number; point: Point }
	private keyboardActive = false
	private wheelSettle$ = new Subject<void>()
	private zoomTransition$ = new Subject<void>()

	private get isGallery(): boolean {
		return this.images.length > 1
	}

	private get currentImageSrc(): string {
		return this.images.length > 0 ? (this.images[this.currentIndex] ?? '') : this.src
	}

	private get currentLabel(): string {
		return this.labels[this.currentIndex] || this.label || componentText('lightbox.image')
	}

	private get reducedMotion(): boolean {
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches
	}

	connectedCallback(): void {
		super.connectedCallback()
		this.currentIndex = this.clampIndex(this.initialIndex)
		merge(
			// `stack.at(-1)` alone assumes the lightbox's own overlay entry IS the
			// topmost one — true only when nothing else is stacked above it. A real
			// `show()` call for this component nests its own `<schmancy-overlay>`
			// host, whose shadow root mounts BOTH the dialog-role surface and a
			// top-layer `[popover]` shell for the same single stack entry — neither
			// changes which entry is "mine". What can genuinely change is whether
			// some OTHER overlay opened after this one and hasn't closed — that
			// overlay owns the keyboard until it does. Find this lightbox's own
			// entry anywhere in the stack (not just the top) and gate on nothing
			// sitting above it; a lightbox mounted standalone (no enclosing
			// overlay at all — the local Escape handler exists for exactly that
			// case) has no own entry to find and stays keyboard-active.
			openOverlays$.pipe(
				map(stack => {
					const ownIndex = stack.findIndex(entry => entry.element.shadowRoot?.contains(this) ?? false)
					return ownIndex === -1 || ownIndex === stack.length - 1
				}),
				distinctUntilChanged(),
				tap(topmost => (this.keyboardActive = topmost)),
			),
			fromEvent<KeyboardEvent>(document, 'keydown').pipe(
				filter(event => this.keyboardActive && !this.isEditingTarget(event)),
				tap(event => this.handleKey(event)),
			),
			// The stage is static in the template, so it exists once the first render
			// commits — and at once on a reconnect, which re-arms these with it.
			from(this.updateComplete).pipe(
				map(() => this.stage),
				filter((stage): stage is HTMLElement => stage !== null),
				switchMap(stage =>
					merge(
						fromEvent<PointerEvent>(stage, 'lostpointercapture').pipe(tap(event => this.pointerCancel(event))),
						fromEvent<WheelEvent>(stage, 'wheel', { passive: false }).pipe(tap(event => this.handleWheel(event))),
					),
				),
			),
			// A mobile URL bar showing or hiding fires resize mid-pan. The zoom the
			// reader chose survives it; only the pan is re-clamped to the new box.
			fromEvent(window, 'resize').pipe(
				tap(() => {
					this.clampPan()
					this.applyTransform()
				}),
			),
			this.wheelSettle$.pipe(
				debounceTime(WHEEL_SETTLE_IDLE_MS),
				tap(() => this.settleZoom()),
			),
			// Debounced, so the clear follows the LATEST discrete change: a clear
			// scheduled by an earlier one would cut the later transition mid-flight.
			this.zoomTransition$.pipe(
				debounceTime(ZOOM_TRANSITION_MS),
				map(() => this.media),
				filter((media): media is HTMLImageElement => media !== null),
				tap(media => media.style.setProperty('--lightbox-zoom-duration', '0ms')),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	disconnectedCallback(): void {
		this.overlayOpacityAnimation?.cancel()
		super.disconnectedCallback()
	}

	/** @internal The overlay's fade must target the filtered image, never its ancestor. */
	animateOverlayOpacity(direction: 'in' | 'out', options: KeyframeAnimationOptions): Animation | undefined {
		const media = this.media
		if (!media) return undefined
		const opacity = getComputedStyle(media).opacity
		this.overlayOpacityAnimation?.cancel()
		media.style.setProperty('--lightbox-fade-duration', '0ms')
		media.style.opacity = direction === 'in' ? '1' : opacity
		this.overlayOpacityAnimation = media.animate(
			direction === 'in' ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity }, { opacity: 0 }],
			options,
		)
		return this.overlayOpacityAnimation
	}

	protected willUpdate(changed: PropertyValues): void {
		if (changed.has('initialIndex') || changed.has('images')) {
			this.currentIndex = this.clampIndex(this.initialIndex)
			this.resetView()
		}
		if (changed.has('src') || changed.has('currentIndex')) {
			this.loading = true
			this.failed = false
		}
	}

	/**
	 * The picture's own dimensions, read off the element rather than off the
	 * event that announced them — because an image that is ALREADY complete by
	 * the time the listener is attached never delivers one. Measured: a data:
	 * URI reports `complete` true synchronously inside the `src` assignment, and
	 * this component's `@load` binding then never ran at all, so `loading` stayed
	 * true and `--lightbox-aspect` went unwritten. `.media` declares
	 * `aspect-ratio: var(--lightbox-aspect, 1)`, and a non-`auto` ratio REPLACES
	 * a replaced element's intrinsic one, so the unwritten fallback rendered every
	 * such photograph square. Ordering the listener before the src binding does
	 * not close it — measured, that changed nothing.
	 *
	 * Called from `@load` for the ordinary fetch and from `updated()` for the one
	 * that was already there; running twice writes the same two values.
	 */
	private settlePicture = (): void => {
		const media = this.media
		if (!media || !media.complete || !media.naturalWidth || !media.naturalHeight) return
		this.loading = false
		// Written imperatively, never as a binding: a later render rewrites a bound
		// style attribute whole and would take the live drag's own opacity with it.
		// On the HOST rather than on `.frame` because `.frame` sits inside a
		// conditional branch lit is free to rebuild — a rebuild dropped the property
		// and the picture went back to square. A custom property inherits, so
		// `.media` reads it from here either way.
		this.style.setProperty('--lightbox-aspect', `${media.naturalWidth} / ${media.naturalHeight}`)
	}

	protected updated(changed: PropertyValues): void {
		// AFTER the update settles, never inside it: `settlePicture` clears
		// `loading`, and a property set from `updated()` is Lit's
		// `change-in-update` warning on every already-complete picture. The extra
		// render it schedules is real and cannot be avoided — the component only
		// learns the file was already there once the element exists to ask.
		void this.updateComplete.then(this.settlePicture)
		if (!changed.has('currentIndex') && !changed.has('images')) return
		if (!this.isGallery) return
		for (const index of [this.currentIndex - 1, this.currentIndex + 1]) {
			if (index < 0 || index >= this.images.length) continue
			const image = new Image()
			image.src = this.images[index]
		}
	}

	private clampIndex(index: number): number {
		const last = Math.max(0, this.images.length - 1)
		return Math.min(last, Math.max(0, Number.isFinite(index) ? Math.trunc(index) : 0))
	}

	private handleKey(event: KeyboardEvent): void {
		switch (event.key) {
			case 'ArrowLeft':
				if (!this.isGallery || this.scale > MIN_SCALE) return
				event.preventDefault()
				this.previous()
				return
			case 'ArrowRight':
				if (!this.isGallery || this.scale > MIN_SCALE) return
				event.preventDefault()
				this.next()
				return
			case '+':
			case '=':
				event.preventDefault()
				this.zoomBy(ZOOM_STEP)
				return
			case '-':
			case '_':
				event.preventDefault()
				this.zoomBy(-ZOOM_STEP)
				return
			case '0':
				event.preventDefault()
				this.resetView()
				return
			case 'Escape':
				// `show()` already wires Escape to dismiss the overlay this element
				// mounts inside of; this is a redundant local escape hatch for a
				// lightbox used standalone, outside an overlay.
				this.close()
				return
			default:
				return
		}
	}

	private isEditingTarget(event: KeyboardEvent): boolean {
		return event
			.composedPath()
			.some(
				target =>
					target instanceof HTMLElement && (target.matches('input, textarea, select') || target.isContentEditable),
			)
	}

	// No resetView() first: the image jumping back to centre while the
	// overlay's exit plays reads as a glitch, not a close.
	private close = (): void => {
		this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }))
	}

	/** Returns whether the gallery moved — a single image has nowhere to go. */
	private previous = (): boolean => {
		if (!this.isGallery) return false
		this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length
		this.resetView()
		this.emitChange()
		return true
	}

	/** Returns whether the gallery moved — a single image has nowhere to go. */
	private next = (): boolean => {
		if (!this.isGallery) return false
		this.currentIndex = (this.currentIndex + 1) % this.images.length
		this.resetView()
		this.emitChange()
		return true
	}

	private emitChange(): void {
		this.dispatchEvent(
			new CustomEvent<LightboxChangeDetail>('change', {
				detail: { index: this.currentIndex },
				bubbles: true,
				composed: true,
			}),
		)
	}

	private emitZoom(): void {
		this.dispatchEvent(
			new CustomEvent<LightboxZoomDetail>('zoom', {
				detail: { scale: this.scale },
				bubbles: true,
				composed: true,
			}),
		)
	}

	/** A settled zoom change — not a per-frame pinch delta. Drives both the
	 *  `zoom` event and the throttled screen-reader announcement. */
	private settleZoom(): void {
		this.emitZoom()
		this.zoomAnnouncement = componentText('lightbox.zoomLevel', { percent: Math.round(this.scale * 100) })
	}

	private zoomBy = (amount: number): void => {
		this.scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, this.scale + amount))
		this.clampPan()
		this.animateTransform()
		this.settleZoom()
	}

	private resetView = (): void => {
		this.scale = MIN_SCALE
		this.panX = 0
		this.panY = 0
		this.pointers.clear()
		this.gesture = undefined
		this.dragIntent = 'undecided'
		this.dragDistance = 0
		this.writeDrag(AT_REST, 0, '')
		this.animateTransform()
		this.settleZoom()
	}

	/** Toggle between 1x and `DOUBLE_TAP_SCALE`, anchored at the tapped point —
	 *  the iOS Photos convention: double-tap zooms in on where you tapped and
	 *  zooms back out to fit on the second double-tap. */
	private toggleZoomAt(point: Point): void {
		if (!this.stage) return
		const rect = this.stage.getBoundingClientRect()
		if (this.scale > MIN_SCALE) {
			this.scale = MIN_SCALE
			this.panX = 0
			this.panY = 0
		} else {
			const anchorX = point.x - (rect.left + rect.width / 2)
			const anchorY = point.y - (rect.top + rect.height / 2)
			this.scale = DOUBLE_TAP_SCALE
			this.panX = anchorX * (1 - DOUBLE_TAP_SCALE)
			this.panY = anchorY * (1 - DOUBLE_TAP_SCALE)
			this.clampPan()
		}
		this.animateTransform()
		this.settleZoom()
	}

	private clampPan(): void {
		const stage = this.stage
		const media = this.media
		if (!stage || !media) {
			this.panX = 0
			this.panY = 0
			return
		}
		// NO RESET AT 1x. This used to zero the pan the instant the scale touched
		// MIN_SCALE, and the pinch and wheel paths write with transition:none so they
		// can track the hand — so zooming back out teleported the picture to where it
		// opened, mid-gesture, with no motion at all. The picture stays where it was
		// put; the clamp below is the only thing that moves it, and only to keep it
		// reachable. resetView() still recentres, because that is someone asking for
		// it — the 0 key, or arriving at a new picture in a gallery.
		// Bounded by the STAGE, not by the picture's own edges. Subtracting the stage
		// stopped the pan the moment the picture's edge reached the stage's, so a
		// zoomed picture could only ever be shuffled inside its own bounds — it could
		// not be moved around the screen, which is the whole point of panning a
		// picture bigger than the window. Adding it lets the picture travel anywhere
		// over the stage; only PAN_KEEP_VISIBLE_PX of it is held on screen, so it can
		// always be grabbed back.
		const maxX = Math.max(0, (media.clientWidth * this.scale + stage.clientWidth) / 2 - PAN_KEEP_VISIBLE_PX)
		const maxY = Math.max(0, (media.clientHeight * this.scale + stage.clientHeight) / 2 - PAN_KEEP_VISIBLE_PX)
		this.panX = Math.min(maxX, Math.max(-maxX, this.panX))
		this.panY = Math.min(maxY, Math.max(-maxY, this.panY))
	}

	/** Writes the current pan/scale straight to `.media`'s style — the live-drag
	 *  path, called every pointermove. No `requestUpdate`, no re-render. */
	private applyTransform(): void {
		const media = this.media
		if (!media) return
		media.style.setProperty('--lightbox-x', `${this.panX}px`)
		media.style.setProperty('--lightbox-y', `${this.panY}px`)
		media.style.setProperty('--lightbox-scale', `${this.scale}`)
	}

	/** Same write, but with a CSS transition — for a programmatic, discrete
	 *  change (keyboard zoom, double-tap, reset, return to centre) rather than a
	 *  finger tracking 1:1 with the pointer, which a transition would only lag. */
	private animateTransform(): void {
		this.overlayOpacityAnimation?.cancel()
		const media = this.media
		if (media) media.style.setProperty('--lightbox-zoom-duration', `${this.reducedMotion ? 0 : ZOOM_TRANSITION_MS}ms`)
		this.applyTransform()
		this.zoomTransition$.next()
	}

	/**
	 * The drag's one write: where the picture is, and how present it still is.
	 *
	 * The transform lands on this host rather than on the picture, because the
	 * host's box IS the viewport under the viewer layout — which is what makes
	 * the inherited origin percentages resolve to the same point the surface
	 * bloomed out of, with no rect read during the drag. `.media` keeps its own
	 * transform for pan, zoom and paging; the two never write the same property
	 * on the same element.
	 *
	 * The fade shares the image's backdrop-filter so it does not create an
	 * ancestor backdrop root. Its transition clock is independent of zoom's;
	 * a settled zoom must not cancel a dismissal's fade.
	 *
	 * `durationMs: 0` is the live path, called every pointermove with no
	 * transition so the picture tracks the hand exactly. Under reduced motion the
	 * picture still crossfades — an opacity dissolve is what the platform's own
	 * guidance substitutes for a transform — while the transform snaps, because
	 * motion is the thing being reduced.
	 */
	private writeDrag(target: DragEnd, durationMs: number, easing: string): void {
		this.style.transition = durationMs > 0 && !this.reducedMotion ? `transform ${durationMs}ms ${easing}` : 'none'
		this.style.transform = `translate(${target.tx}px, ${target.ty}px) scale(${target.scale})`
		const media = this.media
		if (!media) return
		media.style.setProperty('--lightbox-fade-duration', `${durationMs}ms`)
		media.style.setProperty('--lightbox-fade-easing', easing || 'linear')
		media.style.opacity = String(target.opacity)
	}

	/**
	 * The point this viewer bloomed out of, in viewport px.
	 *
	 * Recovered from the origin percentages the overlay wrote, against the
	 * viewport — never from a `getBoundingClientRect()`, which returns the
	 * POST-transform box and would walk the origin a few pixels every frame,
	 * collapsing the pinned-origin invariant the whole law rests on. Read once,
	 * at the press.
	 */
	private bloomOrigin(): Point {
		const style = getComputedStyle(this)
		const percent = (name: string): number => {
			const parsed = Number.parseFloat(style.getPropertyValue(name))
			return Number.isFinite(parsed) ? parsed : 50
		}
		// Against THIS element's own box, in viewport coordinates — which is what the
		// CSS `transform-origin` above resolves the very same percentages against.
		// It used to multiply them by the window, which agreed only while the viewer
		// was full-bleed and its host box WAS the viewport. The surface is
		// content-sized now, so the two disagreed by the host's offset and by the
		// ratio of its box to the window, the translate term stopped cancelling, and
		// the picture drifted out from under the grip instead of shrinking around it.
		// Read once, at the press, while the host is still at identity.
		const rect = this.getBoundingClientRect()
		return {
			x: rect.left + (percent('--schmancy-overlay-origin-x') / 100) * rect.width,
			y: rect.top + (percent('--schmancy-overlay-origin-y') / 100) * rect.height,
		}
	}

	private pointerDown = (event: PointerEvent): void => {
		if (this.failed) return
		this.overlayOpacityAnimation?.cancel()
		// Also the claim that keeps an enclosing sheet's swipe-to-dismiss from
		// starting on this press: that recognizer skips a prevented pointerdown.
		event.preventDefault()
		const point = { x: event.clientX, y: event.clientY }
		this.pointers.set(event.pointerId, point)
		try {
			this.stage?.setPointerCapture(event.pointerId)
		} catch {
			// Synthetic pointer events and older WebKit can reject capture.
		}
		if (this.media) this.media.style.setProperty('--lightbox-zoom-duration', '0ms')
		if (this.pointers.size === 1) {
			this.gestureStartTime = event.timeStamp
			this.dragIntent = 'undecided'
			this.dragDistance = 0
			this.dragVelocity.reset()
			this.pressViewportHeight = window.innerHeight
			this.pressOrigin = this.bloomOrigin()
			this.gesture =
				this.scale > MIN_SCALE
					? { kind: 'pan', start: point, origin: { x: this.panX, y: this.panY } }
					: { kind: 'swipe', start: point }
			return
		}
		if (this.pointers.size === 2 && this.stage) {
			// A swipe's partial drag is a 1x offset a pinch has no use for; anchoring
			// against it would carry that offset into the zoom.
			if (this.gesture?.kind === 'swipe') {
				this.panX = 0
				this.panY = 0
				this.applyTransform()
				// The host too, and instantly: a pinch anchors against the stage's
				// own rect, and a transformed host makes that rect a lie.
				this.writeDrag(AT_REST, 0, '')
			}
			const [first, second] = [...this.pointers.values()]
			const midpoint = this.midpoint(first, second)
			const rect = this.stage.getBoundingClientRect()
			this.gesture = {
				kind: 'pinch',
				distance: Math.max(1, this.distance(first, second)),
				scale: this.scale,
				anchor: {
					x: (midpoint.x - (rect.left + rect.width / 2) - this.panX) / this.scale,
					y: (midpoint.y - (rect.top + rect.height / 2) - this.panY) / this.scale,
				},
			}
		}
	}

	private pointerMove = (event: PointerEvent): void => {
		if (!this.pointers.has(event.pointerId) || !this.gesture) return
		event.preventDefault()
		this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
		if (this.gesture.kind === 'pan') {
			this.panX = this.gesture.origin.x + event.clientX - this.gesture.start.x
			this.panY = this.gesture.origin.y + event.clientY - this.gesture.start.y
			this.clampPan()
			this.applyTransform()
			return
		}
		if (this.gesture.kind === 'swipe') {
			const dx = event.clientX - this.gesture.start.x
			const dy = event.clientY - this.gesture.start.y
			if (this.dragIntent === 'undecided') {
				this.dragIntent = lockIntent(dx, dy, this.isGallery)
				// Paging is not a dismissal and must not look like one, so the shrink
				// the first pixels already answered with is given back before the page
				// starts moving.
				if (this.dragIntent === 'page') this.writeDrag(AT_REST, ZOOM_TRANSITION_MS, 'ease-out')
			}
			const state = dragState(
				{
					dx,
					dy,
					viewportHeight: this.pressViewportHeight,
					grab: this.gesture.start,
					origin: this.pressOrigin,
					gallery: this.isGallery,
					reducedMotion: this.reducedMotion,
				},
				this.dragIntent,
			)
			this.dragDistance = state.distance
			this.dragVelocity.sample(event.timeStamp, state.distance)
			if (this.dragIntent === 'page') {
				this.panX = dx
				this.panY = dy
				this.applyTransform()
				return
			}
			this.writeDrag(state, 0, '')
			return
		}
		if (this.pointers.size < 2 || !this.stage) return
		const [first, second] = [...this.pointers.values()]
		const midpoint = this.midpoint(first, second)
		const rect = this.stage.getBoundingClientRect()
		this.scale = Math.min(
			MAX_SCALE,
			Math.max(MIN_SCALE, this.gesture.scale * (this.distance(first, second) / this.gesture.distance)),
		)
		this.panX = midpoint.x - (rect.left + rect.width / 2) - this.gesture.anchor.x * this.scale
		this.panY = midpoint.y - (rect.top + rect.height / 2) - this.gesture.anchor.y * this.scale
		this.clampPan()
		this.applyTransform()
	}

	/** Trackpad gestures never reach `pointerdown`/`pointermove` — a trackpad
	 *  has no touchscreen, so the OS synthesizes `wheel` instead: pinch as
	 *  `wheel` with `ctrlKey: true` (the browser sets this even though the
	 *  user isn't holding Ctrl), plain two-finger pan as an ordinary `wheel`.
	 *  Continuous like the pointer-pinch path, so it writes through the same
	 *  live `clampPan()`/`applyTransform()` pair rather than the transitioned
	 *  `animateTransform()`, and only settles (announces, fires `zoom`) once
	 *  `wheelSettle$`'s idle debounce fires — a wheel gesture has no discrete
	 *  end event the way a pinch has `pointerup`. Listened to with
	 *  `passive: false`, so `preventDefault()` keeps the browser's own page zoom
	 *  out of a trackpad pinch. */
	private handleWheel = (event: WheelEvent): void => {
		if (!this.stage || !this.media) return
		if (event.ctrlKey) {
			this.overlayOpacityAnimation?.cancel()
			event.preventDefault()
			this.media.style.setProperty('--lightbox-zoom-duration', '0ms')
			const rect = this.stage.getBoundingClientRect()
			const anchorX = (event.clientX - (rect.left + rect.width / 2) - this.panX) / this.scale
			const anchorY = (event.clientY - (rect.top + rect.height / 2) - this.panY) / this.scale
			this.scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, this.scale - event.deltaY * WHEEL_ZOOM_SENSITIVITY))
			this.panX = event.clientX - (rect.left + rect.width / 2) - anchorX * this.scale
			this.panY = event.clientY - (rect.top + rect.height / 2) - anchorY * this.scale
			this.clampPan()
			this.applyTransform()
			this.wheelSettle$.next()
			return
		}
		if (this.scale === MIN_SCALE) return
		this.overlayOpacityAnimation?.cancel()
		event.preventDefault()
		this.media.style.setProperty('--lightbox-zoom-duration', '0ms')
		this.panX -= event.deltaX
		this.panY -= event.deltaY
		this.clampPan()
		this.applyTransform()
		this.wheelSettle$.next()
	}

	private pointerUp = (event: PointerEvent): void => this.finishPointer(event, false)
	/** pointercancel and lostpointercapture: the platform ended the press, so
	 *  its position scores no tap, navigation, or dismissal. */
	private pointerCancel = (event: PointerEvent): void => this.finishPointer(event, true)

	private finishPointer(event: PointerEvent, cancelled: boolean): void {
		// pointerup's releasePointerCapture fires lostpointercapture for the same
		// pointer; that second, cancelled pass must not end a gesture another
		// finger is still driving.
		if (!this.pointers.has(event.pointerId)) return
		const gesture = this.gesture
		const wasSinglePointer = this.pointers.size === 1
		this.pointers.delete(event.pointerId)
		try {
			if (this.stage?.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId)
		} catch {
			// Capture may already have been lost by the platform.
		}

		if (!cancelled && gesture?.kind === 'pinch') this.settleZoom()

		if (wasSinglePointer && gesture && gesture.kind !== 'pinch') {
			const dx = event.clientX - gesture.start.x
			const dy = event.clientY - gesture.start.y
			// ONE decider. What used to stand here scored |dy| against a threshold
			// of its own, 2px away from the law's commit distance and blind to the
			// speed of the release — two answers to one question on one drag.
			const committed =
				gesture.kind === 'swipe' &&
				!cancelled &&
				this.dragIntent === 'dismiss' &&
				commits({
					distance: this.dragDistance,
					velocity: this.dragVelocity.velocityAt(event.timeStamp),
					viewportHeight: this.pressViewportHeight,
				})
			const isTap =
				!cancelled &&
				Math.hypot(dx, dy) < TAP_MOVE_THRESHOLD &&
				event.timeStamp - this.gestureStartTime < TAP_MAX_DURATION
			// A tap on the ground beside the picture is "done with this", the way
			// tapping outside any other overlay is — and the stage fills whatever
			// box the viewer was given, so that ground belongs to this element and
			// nothing above it can hear the tap: the overlay's own outside-click
			// path classifies by whether the surface is in the composed path, and
			// under a full-viewport viewer every tap in the window is.
			//
			// Only at 1x, and only genuinely beside the picture. Zoomed, the ground
			// is where a pan ends up and a tap there is not a dismissal; over the
			// picture, the tap belongs to the double-tap zoom, which needs its first
			// half to survive. The image's own rect, not the pointerdown target:
			// the stage takes pointer capture at press, so by pointerup the target
			// is the stage wherever the finger actually is.
			const mediaRect = isTap && this.scale === MIN_SCALE ? this.media?.getBoundingClientRect() : undefined
			const besideTheImage =
				!!mediaRect &&
				(event.clientX < mediaRect.left ||
					event.clientX > mediaRect.right ||
					event.clientY < mediaRect.top ||
					event.clientY > mediaRect.bottom)
			if (besideTheImage) this.close()
			const zoomed = !besideTheImage && isTap && this.handleTap({ x: event.clientX, y: event.clientY })
			if (this.dragIntent === 'page' && !zoomed && !besideTheImage) {
				// Only a gallery ever locks here, and only sideways: the axis lock has
				// already ruled out the vertical run, so the direction test it used to
				// repeat is gone with it.
				const navigated = !cancelled && Math.abs(dx) > SWIPE_THRESHOLD && (dx > 0 ? this.previous() : this.next())
				// A page that did not reach the next work leaves the picture where the
				// finger dragged it unless it is sent back.
				if (!navigated) {
					this.panX = 0
					this.panY = 0
					this.animateTransform()
				}
			}
			// Last, so every rect read above sees the picture where the finger
			// actually left it rather than mid-return.
			if (committed) {
				// Off the way it was thrown, on the overlay's own exit clock so the two
				// are one motion rather than a queue: it carries on along the drag,
				// shrinking about the pinned origin, until it is clear of the screen
				// while the surface behind it fades. It used to fly HOME to the bloom
				// origin instead, which read as the picture being pulled back out of
				// the hand that had just thrown it away.
				this.writeDrag(
					dismissed(this.reducedMotion, {
						dx,
						dy,
						viewportWidth: window.innerWidth,
						viewportHeight: this.pressViewportHeight,
					}),
					DURATION_EXIT,
					EASE_IN,
				)
				this.close()
			} else if (gesture.kind === 'swipe') {
				this.writeDrag(AT_REST, SNAP_BACK_MS, EASE_EMPHASIZED_DECELERATE)
			}
		}

		if (this.pointers.size === 1 && this.scale > MIN_SCALE) {
			const remaining = [...this.pointers.values()][0]
			this.gesture = { kind: 'pan', start: remaining, origin: { x: this.panX, y: this.panY } }
		} else if (this.pointers.size === 0 || cancelled) {
			this.gesture = undefined
		}
	}

	/** Returns whether this tap completed a double-tap and so moved the zoom. */
	private handleTap(point: Point): boolean {
		const now = performance.now()
		const isDoubleTap =
			!!this.lastTap &&
			now - this.lastTap.time < DOUBLE_TAP_WINDOW &&
			this.distance(point, this.lastTap.point) < DOUBLE_TAP_DISTANCE
		if (isDoubleTap) {
			this.lastTap = undefined
			this.toggleZoomAt(point)
			return true
		}
		this.lastTap = { time: now, point }
		return false
	}

	private midpoint(first: Point, second: Point): Point {
		return { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 }
	}

	private distance(first: Point, second: Point): number {
		return Math.hypot(second.x - first.x, second.y - first.y)
	}

	protected render() {
		const announcement = componentText('lightbox.announce', {
			label: this.currentLabel,
			current: this.currentIndex + 1,
			total: Math.max(1, this.images.length),
		})
		return html`
			<!-- Nothing here clips. The surface is content-sized and anchored at the
			     click, which is how show() places everything — but a zoom grows the
			     picture past that box, and a dismissal drags it out of it entirely.
			     Clipping to the box would cap the zoom at the picture's own footprint
			     and slide the drag behind an invisible edge. The box stays small so the
			     PLACEMENT is show's; the picture is free to use the whole screen. -->
			<div class="relative isolate grid grid-rows-[minmax(0,1fr)] overflow-visible">
				<div
					class="stage relative col-start-1 row-start-1 grid min-h-0 min-w-0 grid-cols-[minmax(0,1fr)] grid-rows-[minmax(0,1fr)] touch-none select-none place-items-center overflow-visible"
					aria-describedby="lightbox-instructions"
					@pointerdown=${this.pointerDown}
					@pointermove=${this.pointerMove}
					@pointerup=${this.pointerUp}
					@pointercancel=${this.pointerCancel}
				>
					${this.loading && !this.failed
						? html`<div
								class="absolute inset-0 grid place-items-center"
								role="status"
								aria-label=${componentText('lightbox.loading', { label: this.currentLabel })}
							>
								<schmancy-progress
									indeterminate
									aria-label=${componentText('lightbox.loading', { label: this.currentLabel })}
								></schmancy-progress>
							</div>`
						: null}
					${this.failed
						? html`<schmancy-typography type="body" token="lg" role="status">
								${componentText('lightbox.unavailable')}
							</schmancy-typography>`
						: html`<div class="frame">
								<img
									class="media origin-center select-none will-change-transform [transition:transform_var(--lightbox-zoom-duration,0ms)_ease-out,opacity_var(--lightbox-fade-duration,0ms)_var(--lightbox-fade-easing,linear)]"
									style="--lightbox-x:0px;--lightbox-y:0px;--lightbox-scale:1;transform:translate(var(--lightbox-x),var(--lightbox-y)) scale(var(--lightbox-scale))"
									.src=${this.currentImageSrc}
									alt=${this.currentLabel}
									draggable="false"
									@load=${this.settlePicture}
									@error=${() => {
										this.loading = false
										this.failed = true
									}}
								/>
							</div>`}
				</div>

				<!-- NO CLOSE BUTTON. Nothing is drawn over the picture: it is dismissed
				     by dragging it away, by tapping the ground beside it, or by Escape.
				     This is the product owner's ruling, stated repeatedly. -->

				<span id="lightbox-instructions" class="sr-only">${componentText('lightbox.instructions')}</span>
				<span class="sr-only" aria-live="polite">${announcement}</span>
				<span class="sr-only" aria-live="polite">${this.zoomAnnouncement}</span>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-lightbox': SchmancyLightbox
	}
}
